/** jitsi-component
 * This is a javascript component used to manage a Jitsi-meet conference
 * in the context of a LUKE web console application.
 * 
 * This also deals specifically with the ability to pull either
 * an intercom associated with an IGOR kiosk or a external phone number
 * into said conference.
 * 
 * The conference will be associated with a specific IGOR Kiosk thru the name
 * of the conference (i.e. each IGOR Kiosk will be assigned a unique room for
 * the conference it joins by default.)
 */

/* global $, JitsiMeetJS */
// import { string } from 'prop-types';

var JitsiComponent = (function () {

    /*************************************************************************
     * Component variables and constants 
     *************************************************************************/

    // component parameter variables
    let _serverUrl = '';
    let _intercomNumber = '';
    let _roomName = '';

    // configuration constants
    const connectOptions = {
        hosts: {
            domain: 'meet.jitsi',
            muc: 'muc.meet.jitsi', 
            anonymousdomain: 'meet.jitsi'
        },
        bosh: ''
    };
    
    // const initOptions = {
    //     disableAudioLevels: true
    // };

    const confOptions = {
    };

    // component operation variables
    let _connection = null;
    let _localI = 0;
    let _localTracks = [];
    let _igorConference = null;
    let _intercomParticipantId = null;
    const remoteTracks = {};

    // Status flag variables
    let _isInit = false;
    let _isConnected = false;
    let _isJoined = false;

    // Event Mechanism variables
    const LISTENERS = {};
    const EVENT_CONSOLE_CONNECTING = 'jc.connecting';
    const EVENT_CONSOLE_FAILED_TO_CONNECT = 'jc.failed-to-connect';
    const EVENT_CONSOLE_CONNECTED = 'jc.connected';
    const EVENT_CONSOLE_DISCONNECTED = 'jc.disconnected';
    const EVENT_INTERCOM_JOINED = 'jc.intercom-joined';
    const EVENT_INTERCOM_LEFT = 'jc.intercom-left';

    /*************************************************************************
     * Public functions
     * Server connection level
     *************************************************************************/

    function init(serverUrl, intercomNumber, roomName) {
        if (_isInit &&
            _serverUrl === serverUrl &&
            _intercomNumber === intercomNumber &&
            _roomName === roomName
        ) return;
        console.info('JitsiComponent: Setting up or adjusting JitsiComponent.');

        // Setup parameters
        _serverUrl = serverUrl || 'https://dev1.conference1.nuvoola.ai';
        _intercomNumber = intercomNumber || '551';
        _roomName = roomName || 'test';

        // Setup connection objects dependant on parameters
        connectOptions.bosh = _serverUrl + '/http-bind';

        _isInit = true;
    }

    function connect() {
        if (!_isInit) { console.warn('JitsiComponent: WARNING: component not initialized. Canceling connection attempt.'); return; } // Not initialized.
        if (_isConnected) { console.warn('JitsiComponent: WARNING: component already connected.'); return; } // Already connected.
        console.log('JitsiComponent: Configuring the connection.');
        _connection = new JitsiMeetJS.JitsiConnection(null, null, connectOptions);

        _connection.addEventListener(
            JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED,
            onConnectionSuccess);
        _connection.addEventListener(
            JitsiMeetJS.events.connection.CONNECTION_FAILED,
            onConnectionFailed);
        _connection.addEventListener(
            JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED,
            onDisconnect);

        console.log('JitsiComponent: Connecting to the conferencing server.');
        fireEvent(EVENT_CONSOLE_CONNECTING);
        _connection.connect();
    }

    async function disconnect() {
        if (!_isConnected) { console.warn('JitsiComponent: WARNING: component already disconnected from conference server.'); return; }
        console.log('JitsiComponent: Requesting conference disconnect.');
        kickIntercom();
        await disposeOfRemoteTracks();
        await disposeOfLocalTracks();
        await leaveConference();
        _connection.disconnect();
    }

    function inviteThirdParty(number) {
        return inviteSipPhone(number);
    }

    /** addEventListener
     * Add an event listener for a given event type.
     * 
     * Current events are:
     * 
     *  JitsiComponent.EVENT_USER_CONNECTING         Will be invoked once a connection is launched.
     *  JitsiComponent.EVENT_USER_FAILED_TO_CONNECT  Will be invoked if the connection attempt fails.
     *  JitsiComponent.EVENT_USER_CONNECTED          Will be invoked once the user ha been added to the conference.
     *  JitsiComponent.EVENT_USER_DISCONNECTED       Will be invoked once the user has shutdown and left the conference.
     *  JitsiComponent.EVENT_INTERCOM_JOINED         Will be invoked once the intercom has joined the conference.
     *  JitsiComponent.EVENT_INTERCOM_LEFT           Will be invoked once the intercom has left the conference.
     * 
     * Mute and video events and functions will be added in a future task.
     */
    function addEventListener(event, listener) {
        let els = null;
    
        console.info(`JitsiComponent: Adding event listener for '${event}'`);
    
        if (!event) {
            console.error('JitsiComponent: ERROR: missing event parameter.');
            return 0;
        }
    
        // Validate proper and existing events
        if (event === EVENT_CONSOLE_CONNECTING ||
            event === EVENT_CONSOLE_CONNECTED ||
            event === EVENT_CONSOLE_FAILED_TO_CONNECT ||
            event === EVENT_CONSOLE_DISCONNECTED ||
            event === EVENT_INTERCOM_JOINED ||
            event === EVENT_INTERCOM_LEFT ) {
            els = LISTENERS[event];
        } else {
            console.error(`JitsiComponent: ERROR: '${event}' is not an event supported by JitsiComponent.`);
            return 0;
        }
    
        if (!els) LISTENERS[event] = els = [];
    
        if (listener && typeof listener === 'function') {
            els.push(listener);
        } else {
            console.error('JitsiComponent: ERROR: listener is not a function.');
            return 0;
        }
    
        return els.length;
    }
    
    /*************************************************************************
     * Public functions
     * Component flags
     *************************************************************************/

    function isInit() {
        return _isInit;
    }

    function isConnected() {
        return _isConnected;
    }

    function isJoined() {
        return _isJoined;
    }

    /*************************************************************************
     * Private functions
     * Event listeners for Jitsi Events
     *************************************************************************/

    // function onDeviceListChanged() {
    //     console.log('JitsiComponent: Adjusting the list of devices');
    //     // For future use with detecting new devices
    // }

    function onConnectionSuccess() {
        console.log('JitsiComponent: Successfully connected to the conferencing server.');
        _isConnected = true;
        fireEvent(EVENT_CONSOLE_CONNECTED);
        _igorConference = _connection.initJitsiConference(_roomName, confOptions);
        _igorConference.on(JitsiMeetJS.events.conference.TRACK_ADDED, onRemoteTrack);
        _igorConference.on(JitsiMeetJS.events.conference.TRACK_REMOVED, onTrackRemoved);
        _igorConference.on(JitsiMeetJS.events.conference.CONFERENCE_JOINED, onConferenceJoined);
        _igorConference.on(JitsiMeetJS.events.conference.USER_JOINED, id => {
            console.log(`JitsiComponent: User ${id} joined.`);
            remoteTracks[id] = [];
        });
        _igorConference.on(JitsiMeetJS.events.conference.USER_LEFT, onUserLeft);
        _igorConference.on(JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, track => {
            // todo: fire the handler for a remote track being muted
            console.log(`JitsiComponent: User ${track.getType()} - ${track.isMuted()}`);
        });
        _igorConference.on(JitsiMeetJS.events.conference.DISPLAY_NAME_CHANGED, (userID, displayName) => {
            console.log(`JitsiComponent: User ${userID} - changed display name to ${displayName}`);
        });
        _igorConference.on(JitsiMeetJS.events.conference.TRACK_AUDIO_LEVEL_CHANGED, (userID, audioLevel) => {
            console.log(`JitsiComponent: User ${userID} - audio level changed to ${audioLevel}`);
        });
        _igorConference.on(JitsiMeetJS.events.conference.PHONE_NUMBER_CHANGED, () => {
            // This is probably not relevant, but record it in case of issues for debugging.
            console.log(`JitsiComponent: Conference phone number changed - ${_igorConference.getPhoneNumber()} - ${_igorConference.getPhonePin()}.`);
        });
        _igorConference.join();
    }

    function onConnectionFailed() {
        console.log('JitsiComponent: Failed to connect to the conferencing server.');
        fireEvent(EVENT_CONSOLE_FAILED_TO_CONNECT);
        _isConnected = false;
    }

    function onDisconnect() {
        console.log('JitsiComponent: Cleaning up conference.');
        if (!_isConnected) { console.warn('JitsiComponent: WARNING: component already disconnected from conference server.'); return; }
        _connection.removeEventListener(
            JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED,
            onConnectionSuccess);
        _connection.removeEventListener(
            JitsiMeetJS.events.connection.CONNECTION_FAILED,
            onConnectionFailed);
        _connection.removeEventListener(
            JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED,
            onDisconnect);
        _connection = null;
        _isConnected = false;
        fireEvent(EVENT_CONSOLE_DISCONNECTED);
        console.log('JitsiComponent: Disconnected from conference server.')
    }

    /** onLocalTracks
     * This function is called when local tracks are discovered.  It will
     * add the track to the array of tracks and also setup the handlers
     * to handle events from those tracks.
     * 
     * once the tracks are the algorithm needs to try adding them to the
     * conference (if this exists)
     * 
     * The local tracks array will also maintain information related to 
     * whether the track was added to the conference or not.
     */
     function onLocalTracks(tracks) {
        console.log(`JitsiComponent: Adding ${tracks.length} devices to local tracks`)
        for (let i = 0; i < tracks.length; i++, _localI++) {
            // Todo: figure out how to copy the element out of tracks into the object in the localTracks array
            _localTracks[_localI] = {track: tracks[i], isInConference: false};
            // for the added tracks, set up the listeners
            _localTracks[_localI].track.addEventListener(
                JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED,
                audioLevel => console.log(`JitsiComponent: Audio Level local: ${audioLevel}.`));
            _localTracks[_localI].track.addEventListener(
                JitsiMeetJS.events.track.TRACK_MUTE_CHANGED,
                () => console.log('JitsiComponent: Local track muted.'));
            _localTracks[_localI].track.addEventListener(
                JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED,
                () => console.log('JitsiComponent: Local track stoped.'));
            _localTracks[_localI].track.addEventListener(
                JitsiMeetJS.events.track.TRACK_AUDIO_OUTPUT_CHANGED,
                deviceId => console.log(`JitsiComponent: Track audio output device was changed to ${deviceId}.`));
            addLocalToConference(_localI);
        }
    }

    function onRemoteTrack(track) {
        if (track.isLocal()) { console.warn('JitsiComponent: Attempt to treat a local track as a remote track.'); return; } // todo: is this necessary (inherit from example)
        console.log('JitsiComponent: A remote track has joined the conference.');

        const participantId = track.getParticipantId();
        if (!remoteTracks[participantId]) {
            remoteTracks[participantId] = [];
        }
        const idx = remoteTracks[participantId].push(track);
    
        let participant = _igorConference.getParticipantById(participantId);
        if (participant._displayName === _intercomNumber) {
            console.log(`JitsiComponent: Identified the intercom participant: { id: "${participantId}", name: "${participant._displayName}"}`);
            fireEvent(EVENT_INTERCOM_JOINED);
            _intercomParticipantId = participantId;
        }
    
        track.addEventListener(
            JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED, audioLevel => {
                console.log(`JitsiComponent: Remote audio level changed: ${audioLevel}`);
            });
        track.addEventListener(
            JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, () => {
                console.log('JitsiComponent: Remote track muted');
            });
        track.addEventListener(
            JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, () => {
                console.log('JitsiComponent: Remote track stoped');
            });
        track.addEventListener(
            JitsiMeetJS.events.track.TRACK_AUDIO_OUTPUT_CHANGED, deviceId => {
                console.log(`JitsiComponent: Remote track audio output device was changed to ${deviceId}`);
            });
        const id = participantId + track.getType() + idx;
    
        if (track.getType() === 'video') {
            // todo: change this to add video ui element to the correct interface element.
            $('body').append(
                `<video autoplay='1' id='${participantId}video${idx}' />`);
        } else {
            $('body').append(
                `<audio autoplay='1' id='${participantId}audio${idx}' />`);
        }
        track.attach($(`#${id}`)[0]);
    }

    function onTrackRemoved(track) {
        console.log(`JitsiComponent: track removed!!! ${track}`); // todo: use the label of the track.
        // Note: this is called both for local and remote tracks.  The conference disconnection will trigger this for
        // all local and remote tracks added to the conference.
        // todo: figure out if any additional processing is required.  -- _conferenceShutdown ?
    }

    function onConferenceJoined() {
        console.log('JitsiComponent: conference joined!');
        _isJoined = true;
        // Todo: Analyse whether we need to handle multiple mics and multiple videos differently.
        // specifically the local mic (may need to handle case where there is more than one)
        // specifically the local video (if any, also may need to handle the case where there is more than one)
        for (let i = 0; i < _localTracks.length; i++) {
            addLocalToConference(i);
        }
        inviteIntercom();
    }

    /** onUserLeft
     * Fired when a remote user leaves a conference.  We need to detatch
     * that user's tracks.
     * @param id The conference user's id.
     */
     function onUserLeft(id) {
        console.log(`JitsiComponent: User ${id} has left the conference.`);
        if (!remoteTracks[id]) return;
        console.log(`JitsiComponent: Detaching associated remote tracks.`);
        
        const tracks = remoteTracks[id];

        for (let i = 0; i < tracks.length; i++) {
            // note: removing track from UI element
            tracks[i].detach($(`#${id}${tracks[i].getType()}`)); 
        }
        if (id === _intercomParticipantId) fireEvent(EVENT_INTERCOM_LEFT);
    }

    /*************************************************************************
     * Private functions
     * General utility functions
     *************************************************************************/

    async function disposeOfRemoteTracks() {
        for (let id in remoteTracks) {
            for (let i = 0; i < remoteTracks[id].length; i++) {
                disposeOfTrack(remoteTracks[id][i]);
            }
            delete remoteTracks[id];
        }
    }

    async function disposeOfLocalTracks() {
        while (_localTracks.length > 0) {
            disposeOfTrack(_localTracks[0].track);
            console.log(`JitsiComponent: track ${_localTracks[0].track} removed from _localTracks.`);
            _localTracks.splice(0, 1);
            _localI--;
        }
    }

    async function disposeOfTrack(track) {
        console.log(`JitsiComponent: disposing of track ${track}.`);
        await track.dispose();
        console.log(`JitsiComponent: track ${track} disposed of.`);
    }

    async function leaveConference() {
        if (_isJoined) {
            console.log('JitsiComponent: Leaving conference.')
            await _igorConference.leave();
            _isJoined = false;
        } else {
            console.warn('JitsiComponent: Unable to leave conference: not currently in a conference.')
        }
    }

    function getLocalTracks() {
        if (!_isInit) { console.error('JitsiComponent: Unable to handle local tracks - not initialized.'); return; } 
        console.log('JitsiComponent: Aquiring local tracks.');
        JitsiMeetJS.createLocalTracks({ devices: ['audio'] })
            .then(onLocalTracks)
            .catch(error => {
                throw error;
            });
        // Todo: We also need to get local video tracks here.JitsiConference
    }

    /** addLocalToConference
     * This takes a local track and adds it to the conference.
     * This may be done as part of a mass add of existing tracks when the conference is created.
     * Or may happen if the track is discovered after the conference is created, when a track
     * is discovered late.
     * @param localI index of the track to add (note that this is not _localI).
     */
    function addLocalToConference(localI) {
        if (!_isJoined) { return; }
        let track = _localTracks[localI].track;
        if (track.getType() === 'video') {
            if (!_localTracks[localI].isInConference) {
                _igorConference.addTrack(track);
                _localTracks[localI].isInConference = true;
                console.log( `JitsiComponent: Local video track "${track.track.label}" added to conference ${_igorConference.options.name}.`);
            } else {
                console.warn( `JitsiComponent: Conference ${_igorConference.options.name} already has a local video track - not adding "${track.track.label}".`);
            }
        } else {
            // todo: Make sure it's only the audio input going into the conference here
            if (!_localTracks[localI].isInConference) {
                _igorConference.addTrack(track);
                _localTracks[localI].isInConference = true;
                console.log( `JitsiComponent: Local audio input track "${track.track.label}" added to conference ${_igorConference.options.name}.`);
            } else {
                console.warn( `JitsiComponent: Conference ${_igorConference.options.name} already has a local audio track - not adding "${track.track.label}".`);
            }
        }
    }

    function inviteIntercom() {
        return inviteSipPhone(_intercomNumber);
    }
    
    function inviteSipPhone(number){
        let isOk = false;
        if (_igorConference.isSIPCallingSupported()) {
            console.log(`JitsiComponent: Inviting ${number} using SIP gateway into conference ${_igorConference.options.name}.`);
            _igorConference.dial(number);
            // Note: the participantId will be established when the remote track for the intercom is
            // detected in the conference.  This is done in the onRemoteTrack function.
            isOk = true;
        } else {
            console.warn(`JitsiComponent: SIP calling is not supported - canceling invite of: ${number}.`);
        }
        return isOk;
    }

    function kickIntercom() {
        if (_intercomParticipantId) {
            console.log(`JitsiComponent: Kicking the intercom out of the conference`);
            _igorConference.kickParticipant(_intercomParticipantId, 'close');
            _intercomParticipantId = null;
        } else {
            console.log(`JitsiComponent: No intercom in conference.  Can not kick the intercom from the conference.`);
        }
    }

    /**
     * Will fire the specified event to all of the registered event listeners.
     * @param {string} event Event identifier string.
     * @param {any} args args to pass to the listener.
     */
    function fireEvent(event, args) {
        let listeners = null;

        console.info(`JitsiComponent: Firing '${event}', args: ${JSON.stringify(args)}`);
        if (!event) return;

        listeners = LISTENERS[event] || [];

        for (let i = 0; i < listeners.length; i++) {
            let listener = LISTENERS[event][i];
            console.info(`JitsiComponent: Calling listener[${i}] '${event}', args: ${JSON.stringify(args)}`);
            listener.apply(this, args);
        }
    }

    /*************************************************************************
     * Singleton initialization code.
     *************************************************************************/

    // console.info('JitsiComponent: Initializing singleton');
    // JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.ERROR); // turn of all lib-jitsi-meet logging except errors
    // JitsiMeetJS.init(initOptions);
    // JitsiMeetJS.mediaDevices.addEventListener(
    //     JitsiMeetJS.events.mediaDevices.DEVICE_LIST_CHANGED,
    //     onDeviceListChanged);

    /*************************************************************************
     * Public members publishing (singleton pattern)
     *************************************************************************/

    return {
        init: init,
        connect: connect,
        disconnect: disconnect,
        getLocalTracks: getLocalTracks,
        inviteThirdParty: inviteThirdParty,

        isInit: isInit,
        isConnected: isConnected,
        isJoined: isJoined,

        addEventListener: addEventListener,
        EVENT_CONSOLE_CONNECTING: EVENT_CONSOLE_CONNECTING,
        EVENT_CONSOLE_CONNECTED: EVENT_CONSOLE_CONNECTED,
        EVENT_CONSOLE_FAILED_TO_CONNECT: EVENT_CONSOLE_FAILED_TO_CONNECT,
        EVENT_CONSOLE_DISCONNECTED: EVENT_CONSOLE_DISCONNECTED,
        EVENT_INTERCOM_JOINED: EVENT_INTERCOM_JOINED,
        EVENT_INTERCOM_LEFT: EVENT_INTERCOM_LEFT
    }
})();

export default JitsiComponent;
